import { ChangeDetectionStrategy, ChangeDetectorRef, Component, effect, ElementRef, inject, input, output, signal, untracked, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatFormField, MatHint, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { SiaTooltipDirective, SiaTranslatePipe } from '@sia/shared';
import { formatNumber } from 'devextreme/localization';
import { SiaBaseEditorSignalComponent } from '../sia-base-editor/sia-base-editor-signal.component';
import { SiaErrorHintComponent } from '../sia-error-hint/sia-error-hint.component';

@Component({
  selector: 'sia-number-box',
  templateUrl: './sia-number-box.component.html',
  styleUrls: ['./sia-number-box.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SiaErrorHintComponent,
    SiaTooltipDirective,
    SiaTranslatePipe,
    MatFormField,
    FormsModule,
    MatLabel,
    MatInput,
    MatHint,
  ],
})
export class SiaNumberboxComponent extends SiaBaseEditorSignalComponent {
  override cdr = inject(ChangeDetectorRef);

  numberBoxMin = input<number | null | string>(null);
  numberBoxMax = input<number | undefined | null>();
  decimals = input<number>();
  showSteps = input(true);
  format = input<string>();

  enterPressed = output<Event>();
  // Internal state for the input element string representation
  stringValue = signal<string | undefined>(undefined);

  // Access the input element to force value updates if signal change detection skips it
  // We use viewChild to get the element. ID based lookup is backup.
  // Note: viewChild returns a Signal.
  inputElement = viewChild<ElementRef<HTMLInputElement>>('inputInfo');

  private _previousValidValue?: number;
  private _inputTimeout: any;

  constructor() {
    super();

    // Effect to sync model value to stringValue when model changes externally
    effect(() => {
      const v = this.value();
      // Use untracked for dependencies we don't want to trigger this effect

      let newStringVal = '';
      if (v === null || v === undefined) {
        newStringVal = '';
      } else {
        const min = untracked(this.numberBoxMin);
        newStringVal = isNaN(v)
          ? typeof min === 'number'
            ? min.toString()
            : ''
          : v?.toString();
      }

      this.stringValue.set(newStringVal);
    }, { allowSignalWrites: true });
  }

  // Method called from template (ngModelChange)
  onStringValueChange(value: string) {
    // 1. Sync local signal immediately to what user typed (raw)
    this.stringValue.set(value);
    clearTimeout(this._inputTimeout);

    if (!value) {
      // Empty logic
      this._inputTimeout = setTimeout(() => {
        const min = this.numberBoxMin();
        const strVal = this.stringValue();
        if (strVal && typeof min === 'number') {
          this.value.set(parseFloat(strVal));
          return;
        }
        if (typeof min === 'number') {
          this.value.set(min);
          return;
        }
        this.value.set(undefined);
        return;
      }, 1000);
    } else {
      // 2. Formatting/Sanitization logic matching original
      let processedValue = value
        .replace(',', '.')
        .replace(/[^0-9.-]/g, '');

      // Handle multiple dots: keep only the first one
      const parts = processedValue.split('.');
      if (parts.length > 2) {
        processedValue = parts.shift() + '.' + parts.join('');
      }

      const dec = this.decimals();

      if (!dec) {
        processedValue = processedValue.split('.')[0];
      } else if (
        processedValue.endsWith('.') ||
        (processedValue.includes('.') && processedValue.endsWith('0'))
      ) {
        // Allow typing ". " or ".0"
        // We update stringValue to processed (in case replacement happened e.g. ',' -> '.')
        if (processedValue !== value) {
          this.stringValue.set(processedValue);
          this.updateDom(processedValue);
        }
        return;
      } else {
        // Limiting decimals
        const decimalIndex = processedValue.indexOf('.');
        if (decimalIndex !== -1 && processedValue.length - decimalIndex - 1 > dec) {
          processedValue = processedValue.substring(0, decimalIndex + dec + 1);
        }
      }

      // 3. Update UI if we sanitized something
      if (processedValue !== value) {
        this.stringValue.set(processedValue);
        this.updateDom(processedValue);
      }

      const numericValue = parseFloat(processedValue);
      if (processedValue === '-') {
        return;
      }

      if (!isNaN(numericValue)) {
        // We update the value allowing out-of-bounds typing. Strict validation is now in onBlur.
        this.value.set(numericValue);
        this._previousValidValue = numericValue;
      } else {
        // If NaN, revert to previous valid
        if (this._previousValidValue !== undefined) {
          this.value.set(this._previousValidValue);
        }
      }
    }
  }

  onBlur() {
    const v = this.value();
    if (v === null || v === undefined || isNaN(v)) {
      const min = this.numberBoxMin();
      if (typeof min === 'number' && (v === null || v === undefined)) {
        this.value.set(min);
        this.stringValue.set(min.toString());
        this.updateDom(min.toString());
      }
      return;
    }

    const min = this.numberBoxMin();
    const max = this.numberBoxMax();
    let finalValue = v;
    let changed = false;

    if (typeof min === 'number' && finalValue < min) {
      finalValue = min;
      changed = true;
    } else if (typeof max === 'number' && finalValue > max) {
      finalValue = max;
      changed = true;
    }

    if (changed) {
      this.value.set(finalValue);
      const s = finalValue.toString();
      this.stringValue.set(s);
      this.updateDom(s);
    } else {
      const s = finalValue.toString();
      if (this.stringValue() !== s) {
        this.stringValue.set(s);
        this.updateDom(s);
      }
    }
  }

  updateDom(val: string) {
    const inputEl = this.inputElement()?.nativeElement
      ?? document.getElementById(this.id() ?? '') as HTMLInputElement;
    if (inputEl && inputEl.value !== val) {
      inputEl.value = val;
    }
  }

  getFormatValue(): string {
    const v = this.value();
    if (v == null || v == undefined) {
      return '';
    }
    const fmt = this.format() ?? '###,##0,###.##';
    return formatNumber(v, fmt);
  }

  override ngOnDestroy(): void {
    clearTimeout(this._inputTimeout);
    // this._valueChangeNumberTimeout is handled by base effect cleanup
    super.ngOnDestroy();
  }
}
